import React from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface TelementProps {
  theme: any;
  index: number;
  opens: string[];
  keywords: string[];
  package: { id: number; name: string };
  khamsa?: boolean;
  toggleOpen: (key: string) => void;
}

const highlightText = (text: string, keyword: string): React.ReactNode => {
  if (!keyword) return text;
  const upper = text.toUpperCase();
  const kwUpper = keyword.toUpperCase();
  if (!upper.includes(kwUpper)) return text;
  const idx = upper.indexOf(kwUpper);
  return (
    <>
      {text.substring(0, idx)}
      <mark className="bg-yellow-200 dark:bg-yellow-300 text-gray-900 rounded-sm px-0.5">
        {text.substring(idx, idx + keyword.length)}
      </mark>
      {text.substring(idx + keyword.length)}
    </>
  );
};

const Telement: React.FC<TelementProps> = ({
  theme: t,
  index,
  opens,
  keywords,
  package: pkg,
  khamsa,
  toggleOpen,
}) => {
  const keyword = keywords?.[0] ?? "";

  return (
    <div className="border-b border-gray-100 dark:border-gray-800 py-4 last:border-0">
      {/* Package link */}
      <div className="mb-2">
        <Link
          to={`/packages/${pkg.id}`}
          className="text-lg font-bold text-blue-500 hover:underline dark:text-indigo-300"
        >
          {index + 1}.&nbsp;{pkg.name}
        </Link>
      </div>

      {/* Theme name */}
      <h4 className="text-sm font-bold text-gray-900 dark:text-gray-50 mb-1">
        <span className="text-gray-500 dark:text-gray-400 font-semibold">Mövzu: </span>
        {highlightText(t.name, keyword)}
      </h4>
      {t.information && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-2">{t.information}</p>
      )}

      {/* Questions */}
      <div className="space-y-2">
        {t.values.map((va: any, i: number) => {
          const isOpen = opens.includes(`${t.id}-${i}`);
          const lines = va.text.split("/-/");
          const towrite = lines.map((m: string, j: number) => (
            <span key={j}>
              {j ? <span>&nbsp;&nbsp;</span> : null}
              {highlightText(m, keyword)}
              <br />
            </span>
          ));

          return (
            <div key={i}>
              <p className="text-gray-800 dark:text-gray-100 font-semibold text-sm break-words">
                {khamsa ? (
                  <><span className="font-bold">Sual: </span>{towrite}</>
                ) : (
                  <>{i + 1}. {towrite}</>
                )}
              </p>

              {/* Reveal toggle */}
              <button
                onClick={() => toggleOpen(`${t.id}-${i}`)}
                className="flex items-center gap-1 text-blue-500 hover:text-blue-700 transition-colors mt-1 dark:text-indigo-300 dark:hover:text-indigo-200"
                aria-label="Cavabı göstər / gizlət"
              >
                <ChevronDown
                  className={cn(
                    "w-4 h-4 transition-transform duration-300",
                    isOpen && "rotate-180"
                  )}
                />
              </button>

              {/* Collapsible answer */}
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300",
                  isOpen ? "max-h-[1000px] opacity-100 mt-1" : "max-h-0 opacity-0"
                )}
              >
                <div className="space-y-1 text-sm">
                  <p className="text-gray-700 dark:text-gray-200 font-medium">
                    <span className="font-bold text-gray-900 dark:text-gray-50">Cavab: </span>
                    {highlightText(va.answer, keyword)}
                  </p>
                  {va.comment?.trim() && (
                    <p className="text-gray-700 dark:text-gray-200">
                      <span className="font-bold text-gray-900 dark:text-gray-50">Şərh: </span>
                      {highlightText(va.comment, keyword)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Theme-level sources */}
      {t.sources.length > 0 && (
        <div className="mt-3 text-sm">
          <p className="text-gray-700 dark:text-gray-200 break-words">
            <span className="font-bold text-gray-900 dark:text-gray-50">Mənbə: </span>
            {t.sources.map((src: string, i: number) => (
              <React.Fragment key={i}>
                {i > 0 && <><br />&nbsp;&nbsp;&nbsp;&nbsp;</>}
                {t.sources.length > 1 ? `${i + 1}. ${src}` : src}
              </React.Fragment>
            ))}
          </p>
        </div>
      )}

      {/* Theme-level authors */}
      {t.authors.length > 0 && (
        <p className="text-sm text-gray-700 dark:text-gray-200 mt-1">
          <span className="font-bold text-gray-900 dark:text-gray-50">
            {t.authors.length > 1 ? "Müəlliflər: " : "Müəllif: "}
          </span>
          {t.authors.map((a: any, i: number) => (
            <React.Fragment key={i}>
              {i > 0 ? ", " : ""}
              {a.fullname}
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
};

export default Telement;
